import { create } from 'zustand';
import { errorMessage } from '../lib/errors.js';
import {
  type Food,
  type FoodInput,
  type FoodLog,
  type FoodLogInput,
  type LogResult,
  type NutritionGoal,
  type Summary,
  listFoods,
  createFood,
  updateFood,
  deleteFood,
  listDay,
  createLog,
  updateLog,
  deleteLog,
  getSummary,
  getGoal,
  saveGoal,
} from '../api/food.js';
import { searchCatalog, sortCatalog } from '../lib/catalog-search.js';
import { todayInParis } from '../lib/dates.js';

interface CaloriesState {
  /** Day currently displayed. */
  date: string;
  summary: Summary | null;
  entries: FoodLog[];
  foods: Food[];
  loading: boolean;
  foodsLoaded: boolean;
  error: string | null;
  /**
   * Whether a goal exists at all, independent of the day on screen. A goal only
   * applies from its `validFrom`, so every day before it has `kcalTarget: null`
   * exactly like a brand-new account — this flag is what keeps the onboarding
   * CTA from reappearing each time you page back a day.
   */
  hasGoal: boolean | null;

  loadDay: (date?: string) => Promise<void>;
  loadGoal: () => Promise<void>;
  loadFoods: () => Promise<void>;

  addLog: (input: FoodLogInput) => Promise<LogResult>;
  editLog: (
    date: string,
    logId: string,
    patch: { label?: string; kcal?: number; protein?: number | null; grams?: number | null }
  ) => Promise<void>;
  removeLog: (date: string, logId: string) => Promise<void>;

  addFood: (input: FoodInput) => Promise<Food>;
  editFood: (foodId: string, patch: Partial<FoodInput>) => Promise<Food>;
  removeFood: (foodId: string) => Promise<void>;
  searchFoods: (query: string) => Food[];

  setGoal: (input: {
    kcalTarget: number;
    proteinTarget?: number | null;
  }) => Promise<NutritionGoal>;
}

export const useCalories = create<CaloriesState>((set, get) => ({
  date: todayInParis(),
  summary: null,
  entries: [],
  foods: [],
  loading: false,
  foodsLoaded: false,
  error: null,
  hasGoal: null,

  // The date moves optimistically so the header reacts instantly, but it is rolled
  // back if the fetch fails — otherwise the previous day's numbers would sit under
  // the new date's header and read as fact.
  loadDay: async (date) => {
    const previous = get().date;
    const day = date ?? previous;
    set({ loading: true, error: null, date: day });
    try {
      const [summary, dayEntries] = await Promise.all([getSummary(day), listDay(day)]);
      set({ summary, entries: dayEntries.entries, loading: false });
    } catch (err) {
      set({ error: errorMessage(err), loading: false, date: previous });
    }
  },

  // On failure this resolves to `false`, not back to `null`: the view treats null
  // as "still loading" and would otherwise skeleton forever. `false` shows the
  // onboarding CTA, which is the recoverable direction to be wrong in — the error
  // banner says why the rest is missing.
  loadGoal: async () => {
    try {
      set({ hasGoal: (await getGoal()) !== null });
    } catch (err) {
      set({ hasGoal: false, error: errorMessage(err) });
    }
  },

  loadFoods: async () => {
    try {
      set({ foods: await listFoods(), foodsLoaded: true });
    } catch (err) {
      set({ error: errorMessage(err) });
    }
  },

  // Entries update from the response right away; the summary is always re-read
  // from the server. A stale week balance is worth less than 200 ms of waiting.
  addLog: async (input) => {
    const result = await createLog(input);
    const { date, entries } = get();
    if (result.log.date === date) set({ entries: [...entries, result.log] });
    set({ summary: await getSummary(date) });
    if (result.food) void get().loadFoods();
    return result;
  },

  editLog: async (date, logId, patch) => {
    const updated = await updateLog(date, logId, patch);
    set({ entries: get().entries.map((e) => (e.logId === logId ? updated : e)) });
    set({ summary: await getSummary(get().date) });
  },

  removeLog: async (date, logId) => {
    await deleteLog(date, logId);
    set({ entries: get().entries.filter((e) => e.logId !== logId) });
    set({ summary: await getSummary(get().date) });
  },

  addFood: async (input) => {
    const food = await createFood(input);
    set({ foods: sortCatalog([...get().foods, food]) });
    return food;
  },

  editFood: async (foodId, patch) => {
    const updated = await updateFood(foodId, patch);
    set({ foods: get().foods.map((f) => (f.foodId === foodId ? updated : f)) });
    return updated;
  },

  removeFood: async (foodId) => {
    await deleteFood(foodId);
    set({ foods: get().foods.filter((f) => f.foodId !== foodId) });
  },

  searchFoods: (query) => searchCatalog(get().foods, query),

  // Writes a new validFrom rather than mutating the current goal, so past days
  // stay judged against the target that was in force back then.
  setGoal: async (input) => {
    const goal = await saveGoal(input);
    set({ hasGoal: true, summary: await getSummary(get().date) });
    return goal;
  },
}));
